import { useCallback } from 'react'
import { SubmitHandler, useFormContext } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import BackNavLink from 'core/components/navigation/BackNavLink'
import APIService from 'api/APIService'
import { ErrorMessage } from 'types/entity'
import { AuthFormValue } from 'pages/auth/type'
import AnalyticsHelper from 'utils/AnalyticsHelper'
import useNavigation from 'core/hooks/navigation/useNavigation'
import Button from 'core/components/buttons/Button'
import SmartField from 'core/components/form/SmartField'
import Input from 'core/components/inputs/Input'
import { useLocation, useParams } from 'react-router'
import FeatureIcon from 'core/components/icons/FeatureIcon'
import { getErrorMessage, PasswordRulesValidations } from 'utils/AppHelper'
import useAuth from 'core/hooks/auth/useAuth'
import { LINK_EVERLANCE_TERMS_OF_SERVICE } from 'constants/Links'
import {
  AcceptInviteUrl,
  SignUpAboutYouUrl,
  SignUpCreateTeamUrl,
  SignUpStepOneUrl,
  StartUrl
} from 'constants/RouteUrls'

const TermsAndCondition = () => (
  <span className="flex justify-center pt-5">
    By signing up you agree to the
    <a
      className="pl-1 underline"
      target="_blank"
      href={LINK_EVERLANCE_TERMS_OF_SERVICE}
      rel="noreferrer"
    >
      Terms & Conditions
    </a>
  </span>
)

const SignupPage = () => {
  const { search } = useLocation()
  const { replace } = useNavigation()
  const params = useParams()
  const signUpType = params?.type
  const searchParams = new URLSearchParams(search)
  const gt = searchParams.get('gt')
  const group_company_name = searchParams.get('group_company_name')
  const group_image_url = searchParams.get('group_image_url')
  const invite_token = searchParams.get('invite_token')
  const redirect_url = searchParams.get('redirect_url')
  const { control, handleSubmit } = useFormContext<AuthFormValue>()
  const { setUserId, setAuthLoading } = useAuth()

  const {
    mutate,
    isPending: isLoading,
    isError,
    error
  } = useMutation({
    mutationFn: APIService.users.createUser,
    onSuccess: (user) => {
      const { received_invites } = user
      setAuthLoading(true)

      setTimeout(() => {
        setUserId(user.token_id.toString())
      }, 300)

      if (group_company_name) {
        AnalyticsHelper.trackEvent('Sign Up', {
          'Company Name': group_company_name
        })
        replace(SignUpStepOneUrl)
        return
      }

      if (invite_token || !!received_invites?.length) {
        AnalyticsHelper.trackEvent('Sign Up', {
          'Invite Code': invite_token ?? received_invites?.[0].token_id ?? ''
        })
        replace(AcceptInviteUrl)
        return
      }

      if (redirect_url === SignUpCreateTeamUrl) {
        AnalyticsHelper.trackEvent('Sign Up')
        replace(SignUpCreateTeamUrl)
        return
      }

      AnalyticsHelper.trackEvent('Sign Up')
      replace(SignUpAboutYouUrl)
    }
  })

  const onSubmit: SubmitHandler<AuthFormValue> = useCallback(
    (formData) => {
      mutate({
        mixpanel_distinct_id: window.mixpanel?.get_distinct_id?.(),
        gt,
        ...formData
      })
    },
    [gt, mutate]
  )

  const title =
    signUpType === 'accountant' ? 'Accountant Sign Up' : 'Create New Account'

  return (
    <div
      title="signup-page-container"
      className="h-[88vh] flex justify-around items-center"
    >
      <div className="card bg-white mb-5 p-[6vmin]! w-[500px]">
        <div className="mb-5">
          <BackNavLink to={StartUrl} />
        </div>
        <div className="flex justify-center">
          {group_company_name && group_image_url ? (
            <img
              src={group_image_url}
              className="h-20 border-none"
              alt={`${group_company_name} logo`}
            />
          ) : (
            <FeatureIcon name="everlance" />
          )}
        </div>
        <div className="flex justify-center">
          <h3 className="pb-20">{title}</h3>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <SmartField
            controllerProps={{
              control,
              name: 'email',
              rules: {
                required: 'Email is required',
                validate: {
                  noBlank: (value) =>
                    value.trim().length > 0 || 'Email cannot be just whitespace'
                }
              }
            }}
          >
            <Input
              placeholder="email@email.com"
              className="text-lg mb-2 w-full py-3.5"
            />
          </SmartField>
          <SmartField
            controllerProps={{
              control,
              name: 'password',
              rules: PasswordRulesValidations
            }}
          >
            <Input
              type="password"
              placeholder="Enter your password"
              className="text-lg mb-2 w-full py-3.5"
            />
          </SmartField>
          <div className="mb-3">
            {isError && (
              <p className="text-danger">
                {getErrorMessage(
                  error as ErrorMessage,
                  'An unknown error occurred'
                )}
              </p>
            )}
          </div>
          <Button
            variant="primary"
            status={isLoading ? 'loading' : 'idle'}
            type="submit"
            title="submit-button"
            className="w-full"
          >
            Sign Up
          </Button>
        </form>
        <TermsAndCondition />
      </div>
    </div>
  )
}

export default SignupPage
